/**
 * Custom Hook pour la gestion des fournisseurs
 * Architecture 3 couches: Component → Hook (TanStack Query) → API Client
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { suppliersAPI } from '../utils/suppliersAPI';
import type { Supplier } from '../types/models';

// ============================================
// QUERY KEYS
// ============================================

export const supplierKeys = {
  all: ['suppliers'] as const,
  lists: () => [...supplierKeys.all, 'list'] as const,
  list: (filters: { page?: number; search?: string; includeInactive?: boolean }) =>
    [...supplierKeys.lists(), filters] as const,
  details: () => [...supplierKeys.all, 'detail'] as const,
  detail: (id: string) => [...supplierKeys.details(), id] as const,
  stats: (id: string) => [...supplierKeys.all, 'stats', id] as const,
};

// ============================================
// INTERFACES
// ============================================

export interface UseSuppliersFilters {
  page?: number;
  limit?: number;
  search?: string;
  includeInactive?: boolean;
}

export interface UseSuppliersReturn {
  suppliers: Supplier[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  searchQuery: string;
  includeInactive: boolean;

  // Actions
  loadSuppliers: (page?: number, search?: string) => void;
  createSupplier: (supplierData: Partial<Supplier>) => Promise<Supplier>;
  updateSupplier: (supplierId: string, supplierData: Partial<Supplier>) => Promise<void>;
  deleteSupplier: (supplierId: string) => Promise<void>;
  activateSupplier: (supplierId: string) => Promise<void>;
  deactivateSupplier: (supplierId: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setIncludeInactive: (include: boolean) => void;
  reload: () => void;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook principal pour la liste des fournisseurs avec TanStack Query
 */
export function useSuppliersQuery(filters?: UseSuppliersFilters) {
  return useQuery({
    queryKey: supplierKeys.list(filters || {}),
    queryFn: () => suppliersAPI.getAll(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook pour récupérer un fournisseur par ID
 */
export function useSupplier(id: string) {
  return useQuery({
    queryKey: supplierKeys.detail(id),
    queryFn: () => suppliersAPI.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook pour les stats d'un fournisseur
 */
export function useSupplierStats(id: string) {
  return useQuery({
    queryKey: supplierKeys.stats(id),
    queryFn: () => suppliersAPI.getStats(id),
    enabled: !!id,
  });
}

/**
 * Hook pour créer un fournisseur
 */
export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Supplier>) => suppliersAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Fournisseur créé avec succès');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erreur lors de la création');
    },
  });
}

/**
 * Hook pour mettre à jour un fournisseur
 */
export function useUpdateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Supplier> }) =>
      suppliersAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: supplierKeys.lists() });
      toast.success('Fournisseur mis à jour');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erreur lors de la mise à jour');
    },
  });
}

/**
 * Hook pour supprimer un fournisseur
 */
export function useDeleteSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => suppliersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Fournisseur supprimé');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook pour activer un fournisseur
 */
export function useActivateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => suppliersAPI.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Fournisseur activé');
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erreur lors de l'activation");
    },
  });
}

/**
 * Hook pour désactiver un fournisseur
 */
export function useDeactivateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => suppliersAPI.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Fournisseur désactivé');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erreur lors de la désactivation');
    },
  });
}

// ============================================
// HOOK LEGACY (rétro-compatibilité)
// ============================================

/**
 * Hook legacy pour la compatibilité avec l'ancienne API
 * @deprecated Utiliser useSuppliersQuery, useCreateSupplier, etc. à la place
 */
export function useSuppliers(): UseSuppliersReturn {
  const [searchQuery, setSearchQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);

  // Query principale
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useSuppliersQuery({
    page,
    search: searchQuery,
    includeInactive,
  });

  // Mutations
  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const deleteMutation = useDeleteSupplier();
  const activateMutation = useActivateSupplier();
  const deactivateMutation = useDeactivateSupplier();

  // Handlers
  const loadSuppliers = useCallback((newPage?: number, search?: string) => {
    if (newPage !== undefined) setPage(newPage);
    if (search !== undefined) setSearchQuery(search);
  }, []);

  const createSupplier = useCallback(async (supplierData: Partial<Supplier>): Promise<Supplier> => {
    return createMutation.mutateAsync(supplierData);
  }, [createMutation]);

  const updateSupplier = useCallback(async (supplierId: string, supplierData: Partial<Supplier>): Promise<void> => {
    await updateMutation.mutateAsync({ id: supplierId, data: supplierData });
  }, [updateMutation]);

  const deleteSupplier = useCallback(async (supplierId: string): Promise<void> => {
    await deleteMutation.mutateAsync(supplierId);
  }, [deleteMutation]);

  const activateSupplier = useCallback(async (supplierId: string): Promise<void> => {
    await activateMutation.mutateAsync(supplierId);
  }, [activateMutation]);

  const deactivateSupplier = useCallback(async (supplierId: string): Promise<void> => {
    await deactivateMutation.mutateAsync(supplierId);
  }, [deactivateMutation]);

  const reload = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    suppliers: data?.suppliers || [],
    loading: isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    error: error ? (error instanceof Error ? error.message : 'Erreur') : null,
    pagination: data?.pagination || { total: 0, page: 1, limit: 20, pages: 1 },
    searchQuery,
    includeInactive,
    loadSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    activateSupplier,
    deactivateSupplier,
    setSearchQuery,
    setIncludeInactive,
    reload,
  };
}
